/**
 * URL caching utility for optimized restaurant URL generation
 */

import { generateRestaurantUrl } from "./slug"

// Cache for generated URLs to avoid repeated processing
const urlCache = new Map<string, string>()

/**
 * Generate restaurant URL with caching for better performance
 */
export function getCachedRestaurantUrl(
  restaurantName: string,
  restaurantAddress: string,
  restaurantId: number,
  cityId?: number
): string {
  // Create a cache key from the parameters
  const cacheKey = `${restaurantId}-${cityId || "no-city"}`

  // Check cache first
  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey)!
  }

  // Generate URL if not in cache
  const url = generateRestaurantUrl(
    restaurantName,
    restaurantAddress,
    restaurantId,
    cityId
  )

  // Cache the result
  if (urlCache.size > 2000) {
    // Clear cache if it gets too large to prevent memory leaks
    urlCache.clear()
  }
  urlCache.set(cacheKey, url)

  return url
}

/**
 * Generate dish URL with caching
 */
export function getCachedDishUrl(
  restaurantName: string,
  restaurantAddress: string,
  restaurantId: number,
  dishId: number,
  cityId?: number
): string {
  const cacheKey = `${restaurantId}-${dishId}-${cityId || "no-city"}`

  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey)!
  }

  const restaurantUrl = getCachedRestaurantUrl(
    restaurantName,
    restaurantAddress,
    restaurantId,
    cityId
  )
  const dishUrl = `${restaurantUrl}/dish/${dishId}`

  if (urlCache.size > 2000) {
    urlCache.clear()
  }
  urlCache.set(cacheKey, dishUrl)

  return dishUrl
}

/**
 * Clear URL cache (useful for testing or memory management)
 */
export function clearUrlCache(): void {
  urlCache.clear()
}
